<template>
  <form class="contents" @submit.prevent="save">
    <tabs />
    <toolbar>
      <action :icon="ResetIcon" :label="$t('pages.modal.resetPassword.buttonText')" @click="passwordResetModalOpen = true" />
      <action :icon="ResetIcon" :label="$t('pages.modal.resendConfirmation.buttonText')" @click="resendConfirmationModalOpen = true" />
      <action :icon="TrashIcon" :label="$t('pages.users.details.delete.buttonText')" @click="deleteUser" />
    </toolbar>
    <password-reset-modal v-model:open="passwordResetModalOpen" :user="modelValue" />
    <resend-confirmation-modal v-model:open="resendConfirmationModalOpen" :user="modelValue" />
    <menu-bar class="flex justify-center" :hidden="!isDirty">
      <div class="w-card-content flex justify-end gap-x-7.5">
        <custom-button variant="outlined" @click="reset">
          {{ $t('reset') }}
        </custom-button>
        <custom-button variant="primary" type="submit" :busy="loading">
          {{ $t('save') }}
        </custom-button>
      </div>
    </menu-bar>
    <cards class="mb-20">
      <card :title="$t('pages.users.details.title')">
        <section class="card-section">
          <div class="flex gap-x-5 w-full">
            <Multiselect
              v-model="rawUser.salutation"
              class="w-1/2"
              :label="$t('user.salutation')"
              :placeholder="$t('user.salutation')"
              :options="getSalutations"
              :can-deselect="false"
              :can-clear="false"
            />
            <FormInput
              v-model="rawUser.email"
              class="w-1/2"
              type="email"
              :error="errors?.email"
              :label="$t('user.email')"
              placeholder="name@mail.com"
              disabled
            />
          </div>
          <div class="flex gap-x-5 w-full">
            <FormInput
              v-model="rawUser.firstName"
              class="flex-1"
              type="text"
              :error="errors?.firstName"
              :label="$t('user.firstName')"
              :placeholder="$t('user.firstName')"
            />
            <FormInput
              v-model="rawUser.lastName"
              class="flex-1"
              type="text"
              :error="errors?.lastName"
              :label="$t('user.lastName')"
              :placeholder="$t('user.lastName')"
            />
          </div>
          <div class="flex gap-x-5 w-full">
            <FormInput
              v-model="rawUser.phoneNumber"
              class="w-1/2"
              type="tel"
              :error="errors?.phoneNumber"
              :label="$t('user.phoneNumber')"
              :placeholder="$t('user.callNumber')"
            />
            <Multiselect
              v-model="rawUser.language"
              class="w-1/2"
              :label="$t('user.language')"
              :placeholder="$t('user.language')"
              :options="getLanguages"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
            />
          </div>
          <div class="flex gap-x-5 w-full">
            <div class="w-1/2">
              <MultiselectTags
                v-if="canAssignRoles"
                v-model="rawUser.roles"
                :label="$t('user.role')"
                :options="getRoles"
                :object="false"
              />
              <FormInput
                v-else
                :model-value="(rawUser.roles ?? []).filter((el) => el !== 'ROLE_USER').map((role) => $t(`user.roles.${role}`)).join(', ')"
                :label="$t('user.role')"
                disabled
              />
            </div>
            <Switch v-model="rawUser.active" :label="$t('user.activeQ')" />
          </div>
        </section>
      </card>
    </cards>
  </form>
</template>

<script lang="ts" setup>
import TrashIcon from '@images/trash.svg?component';
import CustomButton from '@components/utils/CustomButton.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import Cards from '@components/utils/Cards.vue';
import Card from '@components/utils/Card.vue';
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Multiselect from '@components/utils/Multiselect.vue';
import FormInput from '@components/utils/inputs/Input.vue';
import Switch from '@components/utils/inputs/Switch.vue';
import ResetIcon from '@images/reset.svg?component';
import MultiselectTags from '@utils/MultiselectTags.vue';
import PasswordResetModal from '@pages/modals/PasswordResetModal.vue';
import ResendConfirmationModal from '@pages/modals/ResendConfirmationModal.vue';
import {
  array, boolean, object, string,
} from 'yup';
import { useDeletePrompt } from '@utils/prompt/injects';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import MenuBar from '@/components/utils/MenuBar.vue';
import type { User } from '@/entities/Entities';
import { FetchError, useFetchAction } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';
import { useDirty, useValidation } from '@/hooks/form';
import { syncRef } from '@/hooks/state';
import { enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import { useStore } from '@/store';

const store = useStore();

const isSuperAdmin = store.getters['auth/IS_SUPER_ADMIN'];
const canAssignRoles = computed(() =>
  (store.state.auth.user?.roles?.includes('ROLE_ADMIN') ?? false)
  || (store.state.auth.user?.roles?.includes('ROLE_SUPER_ADMIN') ?? false));

interface Props {
  modelValue: User;
}

const props = defineProps<Props>();

const { t } = useI18n();

const passwordResetModalOpen = ref(false);
const resendConfirmationModalOpen = ref(false);

const [exec, { loading }] = useFetchAction();
const emit = defineEmits(['update:modelValue', 'reload']);
const model = toRef(props, 'modelValue');
const getUserCopy = (currentUser: User) => ({
  ...currentUser,
});
const dirtyRef = syncRef(model, getUserCopy);
const rawUser = syncRef(model, getUserCopy);

const isDirty = useDirty(rawUser, dirtyRef);

const reset = () => {
  user.value = getUserCopy(props.modelValue);
};

async function getRoles() {
  const roles = await enumOptionsProvider('/enums/NonDriverRole', (v) => ({
    label: t(`user.roles.${v}`),
    value: v,
  }))();
  if (isSuperAdmin) {
    return roles;
  }
  return roles.filter((el) => el.value !== 'ROLE_SUPER_ADMIN' && el.value !== 'ROLE_FINANCE_ADMIN');
}

const getLanguages = enumOptionsProvider('/enums/language', (v) => ({
  label: t(`language.${v}`),
  value: v,
}));

const getSalutations = enumOptionsProvider('/enums/salutation', (v) => ({
  label: t(`title.${v}`),
  value: v,
}));

const notify = useNotify();

const schema = object({
  firstName: string().required(t('validation.notEmpty')),
  lastName: string().required(t('validation.notEmpty')),
  email: string().email().required(t('validation.notEmpty')),
  active: boolean(),
  language: string(),
  salutation: string(),
  roles: array(),
  phoneNumber: string()
    .matches(/^\+[1-9]\d{1,14}$/, {
      excludeEmptyString: true,
      message: t('user.validation.phoneNumber'),
    })
    .nullable()
    .optional(),
});

const { data: user, errors, validatedFn } = useValidation(schema, rawUser);

const save = validatedFn(async () => {
  try {
    const newUser = await exec<User>(`/users/${user.value.id}`, {
      method: 'PUT',
      body: {
        id: user.value['@id'],
        salutation: user.value.salutation,
        firstName: user.value.firstName,
        lastName: user.value.lastName,
        email: user.value.email,
        phoneNumber: user.value.phoneNumber || null,
        active: user.value.active,
        language: user.value.language,
        ...(canAssignRoles.value ? { roles: user.value.roles } : {}),
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.users.details.saveSuccess'),
    });

    emit('update:modelValue', newUser);
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
});

const promptDelete = useDeletePrompt();

const deleteUser = async () => {
  try {
    const answer = await promptDelete?.();
    if (!answer) return answer ?? false;

    await exec(`/users/${props.modelValue?.id}`, {
      method: 'DELETE',
    });

    notify?.({
      variant: 'success',
      title: t('deleteSuccess'),
    });
    emit('reload');
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === 'driver_has_box') {
        notify?.({
          variant: 'error',
          title: t('pages.users.details.delete.errorMessage'),
        });
      } else {
        notify?.({
          variant: 'error',
          title: t('deleteError'),
        });
      }
    } else {
      notify?.({
        variant: 'error',
        title: t('deleteError'),
      });
    }
  }
  return true;
};
</script>

<style lang="scss" scoped>
form {
  --card-padding: #{px(30)};
}
.card-section {
  @apply flex flex-col gap-y-7.5 w-full;
}
.form-group {
  @apply flex flex-col gap-y-7.5;
  @apply px-7.5 py-4;
  @apply rounded-4 border border-table-head-gray;
  @apply -ml-7.5;
}
</style>
